<script setup lang="ts">
import { computed } from 'vue';

import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { useQueryClient } from '@tanstack/vue-query';

import type { IEditTrackerCountryForm, ITrackerChangeCountryOptions } from '../../types';
import type { IBulkEditTrackerCountryEmits, IBulkEditTrackerCountryProps } from './types';
import { useTrackerChangeCountry } from '../../composables';
import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS } from '../../constants';
import { useBulkChangeTrackerCountryMutation } from '../../mutations';

const {
  affId,
  selectedIds = [],
  filterId = 0,
  page = 1,
  trackersListQueryKey,
} = defineProps<IBulkEditTrackerCountryProps>();

const emit = defineEmits<IBulkEditTrackerCountryEmits>();

const visible = defineModel<boolean>('visible');

const queryClient = useQueryClient();

const {
  mutateAsync,
  isLoading: isSaveLoading,
  errors,
  clearError,
  clearAllErrors,
} = useBulkChangeTrackerCountryMutation();

const trackerCountryFormOptions = computed<ITrackerChangeCountryOptions>(() => ({
  affId,
  isQueryEnabled: visible.value,
}));

const {
  trackerCountryFormSchema,
  showSavingCountryWarning,
  onChange,
} = useTrackerChangeCountry(trackerCountryFormOptions);

async function onSubmit({ country_code }: IEditTrackerCountryForm): Promise<void> {
  await mutateAsync({
    id: affId,
    tracker: {
      country_code,
      page,
      ids: selectedIds,
      segment_id: filterId,
    },
  });

  if (trackersListQueryKey) {
    void queryClient.invalidateQueries({ queryKey: [trackersListQueryKey] });
  }

  visible.value = false;
}

function onHide(): void {
  showSavingCountryWarning.value = false;
  clearAllErrors();
  emit('hide');
}
</script>

<template>
  <VDialog
    v-model:visible="visible"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRACKER_COUNTRY_TITLE')"
    @hide="onHide"
  >
    <VForm
      :schema="trackerCountryFormSchema"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS, 'EDIT_TRACKER_COUNTRY_SAVE')"
      :is-loading="isSaveLoading"
      reverse-buttons
      :custom-errors="errors"
      @change="onChange"
      @submit="onSubmit"
      @interacted="clearError"
    />
  </VDialog>
</template>
